import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { useModuleEnabled } from "../access/useFeatureAccess";
import { featureFlags } from "../config/featureFlags";

/**
 * Route-level gate for Member Search (module id "MemberSearch", a `route` kind
 * in FEATURE_CATALOG). #96 only gates persona access at the tab level inside
 * MemberProfile — this is the net-new route-level gate. When the active persona
 * does not grant MemberSearch, render a friendly panel instead of the search
 * page. With no active persona, useModuleEnabled returns true (allow-all), so
 * this is invisible until a persona is in effect.
 *
 * Extracted from App.tsx (P6b) so the keep-alive TabbedAppShell can reuse the
 * exact same gate without a circular import back into App.tsx.
 */
export function MemberSearchGate({ children }: { children: ReactNode }) {
  const enabled = useModuleEnabled("MemberSearch");
  if (enabled) return <>{children}</>;
  return (
    <div className="flex items-center justify-center min-h-[60vh] px-6">
      <div className="max-w-sm w-full rounded-xl border border-border bg-card px-6 py-5 text-center">
        <p className="text-base font-semibold text-foreground m-0 mb-1">
          Member Search isn't available for your persona
        </p>
        <p className="text-sm text-muted-foreground m-0">
          Switch to a persona that includes Member Search to look up members.
        </p>
      </div>
    </div>
  );
}

/**
 * Route-level gate for Practice Schedule (ACT-1239, module id "PracticeSchedule",
 * a `route` kind in FEATURE_CATALOG — mirrors MemberSearchGate EXACTLY). Gates on
 * persona ONLY (no VITE flag — Practice Schedule is a real shipping surface,
 * unlike the flag-gated unpublished Worklist). MEDICAL_ASSISTANT is seeded the
 * module (roles.ts); a persona without it sees the friendly panel instead of the
 * schedule (which carries patient names/DOBs and care-gap detail). With no active
 * persona, useModuleEnabled returns true (allow-all seam), so this is invisible
 * until a persona is in effect — and, like every module gate, fails CLOSED on a
 * hard currentUser error. Backstops the /schedule deep link; the shell's nav tab
 * is hidden on the same signal (TabbedAppShell's useModuleEnabled check).
 */
export function PracticeScheduleGate({ children }: { children: ReactNode }) {
  const enabled = useModuleEnabled("PracticeSchedule");
  if (enabled) return <>{children}</>;
  return (
    <div className="flex items-center justify-center min-h-[60vh] px-6">
      <div className="max-w-sm w-full rounded-xl border border-border bg-card px-6 py-5 text-center">
        <p className="text-base font-semibold text-foreground m-0 mb-1">
          Practice Schedule isn't available for your persona
        </p>
        <p className="text-sm text-muted-foreground m-0">
          Switch to a persona that includes Practice Schedule to view the daily
          schedule.
        </p>
      </div>
    </div>
  );
}

/**
 * Route-level gate for the Priority Worklist (P6 host wiring, module id
 * "PriorityWorklist", a `route` kind in FEATURE_CATALOG — mirrors
 * MemberSearchGate). GATED on BOTH the VITE_FEATURE_PATIENT_PRIORITY_INDEX
 * flag (module is unpublished — see WorklistPage.tsx) AND persona, so the
 * route stays invisible with the flag off regardless of persona state.
 */
export function WorklistGate({ children }: { children: ReactNode }) {
  const enabled = useModuleEnabled("PriorityWorklist");
  if (!featureFlags.patientPriorityIndex || !enabled) {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
}

/**
 * Route-level gate for the full-page Hospital/ED view (HospitalEdPage.tsx,
 * mounted at `/hospital-ed`). Mirrors WorklistGate EXACTLY — same
 * `featureFlags.patientPriorityIndex` flag, same `PriorityWorklist` module id.
 * Reuses that module id deliberately: Hospital/ED is the SAME underlying
 * panel and dataset as the Patient Panel (just a different
 * `worklistVariant`), so a separate module id would be a new access concept
 * nobody has provisioned.
 */
export function HospitalEdGate({ children }: { children: ReactNode }) {
  const enabled = useModuleEnabled("PriorityWorklist");
  if (!featureFlags.patientPriorityIndex || !enabled) {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
}
